import {
  ISPGetItemResponse,
  ISPListData
} from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/interfaces/ISPGetItemResponse';
import { getDataWithCache } from './getData';
import {
  getRenderOption,
  getOverrideViewXml,
  getCombinedViewXml
} from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/ListItemPostDataHelpersBasic';
import { IRenderListDataParameters } from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/ListItemPostDataHelpers';
import { getListRequestHeaders } from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/ListItemDataHelpers';
import {
  IItemCacheDispatch,
  IItemCacheThunkAction
} from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { getHasPageLoaded } from '@ms/items-view/lib/private/operations/pageLoad';
import { spPrefetchDataConfigurationFacet, enableModernListSchemasConfigurationFacet } from './Facets';
import { SimpleUri } from '@ms/items-view-utilities/lib/uri/SimpleUri';
import {
  ISPGetItemContext,
  IGetServerDataCallSignature
} from './interfaces/IGetServerData';
import View from '@ms/odsp-datasources/lib/models/view/View';
import { readConfiguration } from '@ms/items-view/lib/private/operations/configuration/readConfiguration';
import { ISPListItemRetrieverResponse } from '@ms/odsp-datasources/lib/SPListItemRetriever';
import ServerData from '@ms/odsp-datasources/lib/dataSources/base/ServerData';
import { getListDataAsStreamUrl } from './listDataAsStream/getListDataAsStreamUrl';

export function getServerDataWithPrefetch(
  serializedItemSetKey: string,
  getItemContext: ISPGetItemContext,
  prefetchFallbackOnComplete: (serverData: ISPListItemRetrieverResponse) => void,
  skipPrefetch?: boolean
): IItemCacheThunkAction<Promise<ISPListItemRetrieverResponse>> {
  return async (dispatch: IItemCacheDispatch) => {
    const {
      prefetchDataConfig
    } = dispatch(readConfiguration({
      prefetchDataConfig: spPrefetchDataConfigurationFacet
    }));

    const prefetchData = prefetchDataConfig && prefetchDataConfig.prefetchData;

    const serverDataCallSignature = dispatch(getServerDataCallSignature(getItemContext));
    const hasPageLoaded = dispatch(getHasPageLoaded());
    const fallBack: () => Promise<ISPListItemRetrieverResponse> = () => {
      return dispatch(getServerData(getItemContext, serverDataCallSignature));
    };
    const fallBackOnComplete: (serverData: ISPListItemRetrieverResponse) => void = (serverData: ISPListItemRetrieverResponse) => {
      prefetchFallbackOnComplete(serverData);
    };
    // do not try prefetch if page has been loaded
    if (!prefetchData || hasPageLoaded || skipPrefetch) {
      return fallBack();
    }
    const response = await prefetchData({
      itemSetKey: serializedItemSetKey,
      serverDataCallSignature,
      fallBack,
      fallBackOnComplete
    });
    // if no valid prefetched data, do normal data fetch
    if (!response) {
      return fallBack();
    }
    return response;
  };
}

export function getServerData(
  context: ISPGetItemContext,
  serverDataSignature?: IGetServerDataCallSignature | undefined
): IItemCacheThunkAction<Promise<ISPListItemRetrieverResponse>> {
  return async (dispatch: IItemCacheDispatch) => {
    const serverData = await dispatch(getDataWithCache<ISPListItemRetrieverResponse>({
      webAbsoluteUrl: context.webAbsoluteUrl,
      listFullUrl: context.listFullUrl,
      qosName: 'getListDataAsStream.' + context.qosName,
      ...(serverDataSignature || dispatch(getServerDataCallSignature(context)))
    }));

    return fixListUrlInServerResponse(serverData, context.listFullUrl);
  };
}

function fixListUrlInServerResponse(serverData: ISPGetItemResponse, listFullUrl: string): ISPGetItemResponse {
  const webAbsoluteUrl = serverData.HttpRoot || new SimpleUri(listFullUrl).authority;
  const webAbsoluteUrlPath = new SimpleUri(webAbsoluteUrl).path;
  const listUrlDirContainsWebUrl = (serverData.listUrlDir || '').slice(0, webAbsoluteUrlPath.length) === webAbsoluteUrlPath;
  return {
    ...serverData,
    listUrlDir: listUrlDirContainsWebUrl ? serverData.listUrlDir : `${webAbsoluteUrlPath}${serverData.listUrlDir}`
  };
}

function getServerDataCallSignature(context: ISPGetItemContext): IItemCacheThunkAction<IGetServerDataCallSignature> {
  return (dispatch: IItemCacheDispatch) => {
    const {
      enableModernListSchemas
    } = dispatch(readConfiguration({
      enableModernListSchemas: enableModernListSchemasConfigurationFacet
    }));

    const urlParams = {
      ...context,
      enableModernListSchemas: !!enableModernListSchemas
    };

    return {
      method: 'POST',
      url: getListDataAsStreamUrl(urlParams),
      body: getPostBody(context),
      additionalHeaders: {
        ...getListRequestHeaders({
          listUrl: new SimpleUri(context.listFullUrl).path
        }, context.postDataContext),
        ...(context.preferredLanguage ? {
          'Accept-Language': context.preferredLanguage
        } : {}),
        'X-ServiceWorker-Strategy': context.skipServiceWorkerCache ?
          'SkipCache' :
          'CacheFirst'
      },
      parseResponse: (responseText: string, serverData: ServerData): ISPListItemRetrieverResponse => {
        const originalResponse: ISPGetItemResponse | ISPListData = JSON.parse(responseText);

        const response = isCompleteListResponse(originalResponse) ? originalResponse : {
          ListData: originalResponse
        };

        const isFromServiceWorkerCache = serverData.getServiceWorkerDataSourceHeader() === 'ServiceWorker-FromCache';

        return {
          ...response,
          isFromServiceWorkerCache
        };
      }
    };
  };
}

function getPostBody(context: ISPGetItemContext): string {
  const renderOption = getRenderOption({
    ...context.postDataContext,
    isListDataRenderOptionChangeFeatureEnabled: true,
    isSpotlightFeatureEnabled: true
  });
  let viewXml = context.viewXml;

  const overrideViewXml = context.groupByField || context.fetchNextGroupXml || context.disableGrouping || context.additionalFiltersXml ?
    getOverrideViewXml({
      groupByField: context.groupByField,
      disableGrouping: context.disableGrouping,
      fetchNextGroupXml: context.fetchNextGroupXml,
      additionalFiltersXml: viewXml ? undefined : context.additionalFiltersXml
    }) : undefined;

  if (viewXml) {
    // update viewXml to include grouping infomation.
    if (context.disableGrouping || context.groupByField) {
      let view = new View(viewXml);
      if (context.disableGrouping) {
        // clear the groupby when context has disableGrouping
        view.updateGroupBy({ group1: { fieldName: '' } });
      } else if (context.groupByField) {
        // merge in grouping information if present
        view.updateGroupBy({ isCollapsed: false, group1: { fieldName: context.groupByField } });
      }
      viewXml = view.getEffectiveViewXml();
    }

    let additionalFiltersXml = context.additionalFiltersXml;
    if (context.fetchNextGroupXml) {
      // merge fetch next group xml into additonalFiltersXml.
      additionalFiltersXml = additionalFiltersXml ?
        '<And>' + additionalFiltersXml + context.fetchNextGroupXml + '</And>' :
        context.fetchNextGroupXml;
    }

    if (additionalFiltersXml) {
      viewXml = getCombinedViewXml({
        viewXml: viewXml,
        additionalFiltersXml: additionalFiltersXml
      });
    }
  }

  let renderParams: IRenderListDataParameters = {
    __metadata: { type: 'SP.RenderListDataParameters' },
    RenderOptions: renderOption > 0 ? renderOption : undefined,
    ViewXml: viewXml || undefined,
    OverrideViewXml: overrideViewXml || undefined,
    AllowMultipleValueFilterForTaxonomyFields:
      context.postDataContext && context.postDataContext.allowMultipleValueFilterForTaxonomyFields,
    AddRequiredFields: true,
    ReplaceGroup: context.isLargeFolder
  };

  return JSON.stringify({ parameters: renderParams });
}

function isCompleteListResponse(response: ISPListData | ISPGetItemResponse): response is ISPGetItemResponse {
  return !!(<ISPGetItemResponse>response).ListData;
}
